import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import useModalEffect from '../../../hooks/useModalEffect';

const emptyForm = {
  accountCode: '',
  accountName: '',
  accountType: '',
  description: ''
};

const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

const ChartOfAccountForm = ({ isOpen, onClose, onSubmit, initialData }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        accountCode: initialData.accountCode || '',
        accountName: initialData.accountName || '',
        accountType: initialData.accountType || '',
        description: initialData.description || ''
      });
    } else {
      setFormData(emptyForm);
    }
    setFormErrors({});
  }, [initialData, isOpen]);

  ;


  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.accountCode.trim()) errors.accountCode = 'Account code is required';
    if (!formData.accountName.trim()) errors.accountName = 'Account name is required';
    return errors;
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    onSubmit(formData);
  };

  return (
    <>
      <style>{`
        #modal-chart-of-account .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-chart-of-account .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-chart-of-account .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-chart-of-account .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-chart-of-account .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-chart-of-account .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-chart-of-account"
        tabIndex="-1"
        role="dialog"
      >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{initialData ? 'Edit Chart of Account' : 'Create Chart of Account'}</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <form onSubmit={handleFormSubmit}>
            <div className="modal-body">
              <div className="inv-section-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                Details
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Account Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={'form-control ' + (formErrors.accountCode ? 'is-invalid' : '')}
                    name="accountCode"
                    value={formData.accountCode}
                    onChange={handleInputChange}
                    placeholder="1000"
                  />
                  {formErrors.accountCode && <div className="invalid-feedback">{formErrors.accountCode}</div>}
                </div>
                <div className="col-md-8">
                  <label className="form-label">Account Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={'form-control ' + (formErrors.accountName ? 'is-invalid' : '')}
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleInputChange}
                    placeholder="Cash and Bank"
                  />
                  {formErrors.accountName && <div className="invalid-feedback">{formErrors.accountName}</div>}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Account Type</label>
                  <select className="form-select" name="accountType" value={formData.accountType} onChange={handleInputChange}>
                    <option value="">Select type</option>
                    {accountTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                <div className="col-md-8">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Finance mapping notes"
                  />
                </div>
              </div>
            </div>
            <div className="inv-footer">
              <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                <span style={{ color: '#9aa5b1' }}>
                  <span className="inv-required-star">*</span> Required fields
                </span>
              </span>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-inv-save">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </>
  );
};

ChartOfAccountForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default ChartOfAccountForm;
